import './index.less';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import * as actions from 'modules/actions/smSupplierManagement';
import { CertificateAttachment } from 'modules/types/commons/file';
import { Row, Col, Button } from '@beehive/components';
import { CardPage, BottomFixed, showAlertHOC } from 'components';
import {
  BasicInfo,
  QualifiedFile,
  ServiceInfo,
  SupplierContacts,
  SupplierEmployees,
  supplierEditHOC,
} from '../components/Edit';
import {
  SupplierContracts,
  BankInfo,
} from '../components/Detail';
import { BANK_INFO_KEYS } from '../Detail';
import { EditProps } from '../components/Edit/supplierEditHOC';
// @ts-ignore
import _ from 'lodash';
import classnames from 'classnames';
import { ScrollHelper } from 'utils';
import PropTypes from 'prop-types';
import messages from 'messages/CompanyManagement';

export const BASIC_FORM_KEYS = {
  short_name: { show: true },
  supplier_type: { show: true },
  region: { show: true },
  parent_company: { show: true },
  address: {},
  advance_payment: {},
  service_type: {},
  service_content: {
    itemColProps: { sm: 12, xxl: 16 },
  },
};
const CANCEL_URL = '/companyManagement/companyInfo';
const NEED_VALID_REFS = [
  'basicInfoRef', 'serviceInfoRef', 'supplierEmployeesRef',
];

class CompanyInfoEdit extends Component<EditProps, any> {

  public static contextTypes = {
    intl: PropTypes.object,
  };

  private basicInfoRef: React.RefObject<BasicInfo>;
  private serviceInfoRef: React.RefObject<ServiceInfo>;
  private supplierEmployeesRef: React.RefObject<SupplierEmployees>;

  constructor(props: EditProps) {
    super(props);
    this.state = {
      showError: false,
    };
    this.basicInfoRef = React.createRef();
    this.serviceInfoRef = React.createRef();
    this.supplierEmployeesRef = React.createRef();
  }

  private getAttachesAttributes = () => {
    const { data } = this.props;
    const { supplier_attaches = [], supplier_attaches_origin = [] } = data;

    const validAttaches = supplier_attaches
      .filter((a: CertificateAttachment) => a.id || (!a.id && a.status !== 'removed'));

    let isAttachesChanged = false;
    if (supplier_attaches_origin.length !== validAttaches.length) {
      isAttachesChanged = true;
    } else {
      supplier_attaches_origin.forEach((i: CertificateAttachment) => {
        const attach = validAttaches.find((j: CertificateAttachment) => j.id === i.id);
        if (!attach || attach.status !== i.status) {
          isAttachesChanged = true;
        }
      });
    }
    if (!isAttachesChanged) {
      return;
    }

    return validAttaches.map((a: CertificateAttachment) => {
      const item = {
        id: a.id,
        status: a.status,
        cert_attach_blob_id: a.cert_attach.blob.id,
        blob_key: _.get(a, 'cert_attach.blob.blob_key'),
      };
      if (item.status === 'removed') {
        item['_destroy'] = true;
      }
      return item;
    });
  }

  private getSupplierEmployeesAttributes = () => {
    const { data } = this.props;
    const supplierEmployees = _.get(data, 'supplier_employees', []);
    const supplierEmployeesAttributes = supplierEmployees.map((item: any) => {
      const newItem = _.pickBy(item, (value: any, key: any) => {
        return !_.startsWith(key, 'user_');
      });
      const userAttributes = _.pickBy(item, (value: any, key: any) => {
        return _.startsWith(key, 'user_');
      });
      return Object.assign(newItem, {
        user_attributes: _.mapKeys(userAttributes, (value: any, key: any) => {
          return key.replace('user_', '');
        }),
      });
    });
    return supplierEmployeesAttributes;
  }

  private handleCancel = () => {
    const { cancelUrl } = this.props;
    this.props.history.push(cancelUrl || CANCEL_URL);
  }

  private handleSave = () => {
    if (NEED_VALID_REFS.some((key: string) => !this[key] || !this[key].current)) {
      return;
    }
    if (NEED_VALID_REFS.some((key: string) => !this[key].current.isFormValid())) {
      this.setState({ showError: true }, () => {
        ScrollHelper.scrollToFormFirstError();
      });
    } else {
      const { handleSave, data } = this.props;
      const { change_detail } = data;
      const supplierAttachesAttributes = this.getAttachesAttributes();
      const supplierEmployeesAttributes = this.getSupplierEmployeesAttributes();
      if (handleSave) {
        handleSave({
          change_detail,
          supplier_attaches_attributes: supplierAttachesAttributes,
          supplier_employees_attributes: supplierEmployeesAttributes,
        });
      }
    }
  }

  private getUploadUrl = () => {
    const { getAttachementUploadUrl } = this.props;

    if (getAttachementUploadUrl) {
      return getAttachementUploadUrl();
    }

    return '/api/v1/attachments/upload';
  }

  public render() {
    const { intl } = this.context;
    const {
      prefixCls,
      data,
      showAlert,
      onChange,
      onSave,
      getDetail,
    } = this.props;
    const {
      showError,
    } = this.state;

    const { saveLoading, saveClicked } = data;
    const classes = classnames({
      [`${prefixCls}-edit`]: true,
    });
    const cardProps = {
      data,
      onChange,
      onSave,
      getDetail,
      showError,
      saveClicked,
    };

    return (
      <CardPage className={classes} bottomSize="default">
        <Row gutter={16} type="flex">
          <Col span={15}>
            <BasicInfo
              {...cardProps}
              customFormKeys={BASIC_FORM_KEYS}
              ref={this.basicInfoRef}
            />
          </Col>
          <Col span={9}>
            <QualifiedFile
              showAlert={showAlert}
              uploadModel="SocialMedia::SupplierAttach"
              getUploadUrl={this.getUploadUrl}
              {...cardProps}
            />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <ServiceInfo
              {...cardProps}
              ref={this.serviceInfoRef}
            />
          </Col>
        </Row>
        {data.showSupplierContracts ? (
          <Row gutter={16} type="flex">
            <Col span={15}>
              <SupplierContracts {...data} />
            </Col>
            <Col span={9}>
              <SupplierContacts {...cardProps} />
              <BankInfo {...data} bankInfoKeys={BANK_INFO_KEYS} inEdit />
            </Col>
          </Row>
        ) : (
          <Row gutter={16} type="flex">
            <Col span={12}>
              <SupplierContacts {...cardProps} />
            </Col>
            <Col span={12}>
              <BankInfo {...data} bankInfoKeys={BANK_INFO_KEYS} inEdit />
            </Col>
          </Row>
        )}
        <Row>
          <Col span={24}>
            <SupplierEmployees {...cardProps} ref={this.supplierEmployeesRef} />
          </Col>
        </Row>
        <BottomFixed>
          <Button
            size="large"
            disabled={saveLoading}
            onClick={this.handleCancel}
          >
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            size="large"
            onClick={this.handleSave}
            type="primary"
            disabled={saveLoading}
            loading={saveLoading}
          >
            {intl.formatMessage(messages.save)}
          </Button>
        </BottomFixed>
      </CardPage>
    );
  }
}

const mapState2Props = (state: any, ownProps: any) => {
  const data = ownProps.data || state.smSupplierManagement.detail;
  const filters = ownProps.filters || state.smSupplierManagement.supplierOptions;
  return {
    data,
    filters,
    isEdit: true,
  };
};
export function mapDispatchToProps(
  dispatch: Dispatch<actions.SupplierManagementActions>,
  ownProps: any,
) {
  return bindActionCreators({
    getDetail: ownProps.getDetail || actions.getDetail,
    onSave: ownProps.onSave || actions.saveDetail,
    onChange: ownProps.onChange || actions.chagneDetail,
  }, dispatch);
}
export default connect(mapState2Props, mapDispatchToProps)
  (showAlertHOC(supplierEditHOC(CompanyInfoEdit, { cancelUrl: CANCEL_URL })));
